import type React from "react";
import { useEffect, useRef, useState } from "react";
import { cn } from "~/lib/utils";

// // // //

export function AnimatedExpandedDetails(props: {
    isExpanded: boolean;
    children: React.ReactNode;
}) {
    const { isExpanded, children } = props;
    const contentRef = useRef<HTMLDivElement | null>(null);
    const [maxHeight, setMaxHeight] = useState("0px");

    useEffect(() => {
        const contentElement = contentRef.current;
        if (!contentElement) {
            return;
        }

        const updateHeight = () => {
            setMaxHeight(
                isExpanded ? `${contentElement.scrollHeight}px` : "0px",
            );
        };

        updateHeight();

        if (!isExpanded) {
            return;
        }

        const resizeObserver = new ResizeObserver(updateHeight);
        resizeObserver.observe(contentElement);

        return () => {
            resizeObserver.disconnect();
        };
    }, [isExpanded, children]);

    return (
        <div
            aria-hidden={!isExpanded}
            className={cn(
                "overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out",
                isExpanded ? "opacity-100" : "pointer-events-none opacity-0",
            )}
            style={{ maxHeight }}
        >
            <div ref={contentRef} className="min-h-0">
                {children}
            </div>
        </div>
    );
}
